import { z } from 'zod';

/**
 * Side-effect module: turns Zod's JIT object parser off, for the browser SPA (steward-app#2352).
 *
 * Why: the SPA's enforced CSP (`script-src 'self' https://cdn.plaid.com https://challenges.cloudflare.com`,
 * the dev response header as read 2026-10-13; no `'unsafe-eval'`) refuses the empty-body `Function`
 * constructor call, which is what Zod's `allowsEval` probe
 * (`zod/v4/core/util.js`, 4.6.5 line 218) runs once per document when the
 * first object schema is built. The throw is caught, but the browser still reports a
 * `securitypolicyviolation` with blocked URI `eval`. Under `jitless` that probe returns `false` before
 * touching `Function` (same file, line 220). Zod's other `Function` site, `Doc.compile`
 * (`v4/core/doc.js`), is reachable only through the JIT fast path or `z.compile`, so it is not called
 * under jitless (a reviewer's reading of zod 4.6.5, 2026-10-13; not tested here).
 *
 * Ordering is the whole point. ES module imports evaluate before the importing module's body, and the
 * schemas in this package are built at module top level, so `z.config(...)` in the body of the SPA entry
 * would run AFTER the probe. This file therefore imports ONLY `zod` (no sibling module, so nothing here
 * builds a schema) and the SPA entry imports it first. It is deliberately not re-exported from `index.ts`,
 * so that code loading the index (the façade API, the Lambdas) keeps the JIT path.
 *
 * What pins this: `zodJitless.test.ts` (config is set, this file's `import` statements are zod-only,
 * and no non-test file in this one flat directory mentions it; that is a text search of this directory,
 * not of what the API or Lambdas actually load) and `src/zodJitlessEntry.test.ts` (first statement of
 * the SPA entry, and no `manualChunks`). What nothing pins: that the bundler keeps the order in the
 * emitted chunk. On 2026-10-13 the implementing session read the built index chunk (config assignment
 * directly after the Zod library code, before React and the schemas) and drove it in headless Chromium
 * under the real script-src: 1 violation on origin/main, 0 here. A point-in-time reading, not an
 * invariant.
 */
z.config({ jitless: true });
